import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, UserPlus, X, Check, Ban, UserMinus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { TeamCard } from "../components/PlayerCard";
import { AchievementGrid } from "../components/AchievementGrid";
import RankedStatsCard from "../components/RankedStatsCard";
import { ModeratorBadge } from "../components/ModeratorBadge";
import { UserName } from "../components/UserName";
import { TierChip } from "./Leaderboards";
import Avatar from "../components/Avatar";
import { countryFlag, fmtMs } from "../lib/derive";
import { countryName } from "../lib/countries";

function FriendshipActions({ summary, onChange }) {
  const [busy, setBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  if (!summary || summary.friendship_status === "self") return null;
  const fs = summary.friendship_status;
  const fid = summary.friendship_id;
  const uid = summary.user.user_id;

  const wrap = async (fn) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); await onChange?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (fs === "none") {
    return (
      <div className="mt-4">
        <button data-testid="pp-add-friend"
                onClick={() => wrap(() => api.post("/friends/request", { to_user_id: uid }))}
                className="px-4 py-2 rounded-full bg-[var(--accent)] text-black font-semibold text-sm inline-flex items-center gap-2">
          <UserPlus size={14}/> Add friend
        </button>
        <button data-testid="pp-block"
                onClick={() => wrap(() => api.post(`/friends/${uid}/block`))}
                className="ml-2 px-3 py-2 rounded-full bg-white/5 border border-white/10 text-xs text-zinc-300 hover:bg-white/10 inline-flex items-center gap-1.5">
          <Ban size={12}/> Block
        </button>
      </div>
    );
  }
  if (fs === "pending_outgoing") {
    return (
      <div className="mt-4 flex items-center gap-2">
        <span className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs text-zinc-400 uppercase tracking-widest">Request pending</span>
        <button data-testid="pp-cancel-req"
                onClick={() => wrap(() => api.post(`/friends/${fid}/cancel`))}
                className="px-3 py-1.5 rounded-full border border-white/10 text-xs hover:bg-white/5 inline-flex items-center gap-1.5">
          <X size={12}/> Cancel
        </button>
      </div>
    );
  }
  if (fs === "pending_incoming") {
    return (
      <div className="mt-4 flex items-center gap-2">
        <button data-testid="pp-accept"
                onClick={() => wrap(() => api.post(`/friends/${fid}/accept`))}
                className="px-4 py-2 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-sm font-semibold inline-flex items-center gap-2">
          <Check size={14}/> Accept
        </button>
        <button data-testid="pp-decline"
                onClick={() => wrap(() => api.post(`/friends/${fid}/decline`))}
                className="px-3 py-2 rounded-full border border-white/10 text-xs hover:bg-white/5 inline-flex items-center gap-1.5">
          <X size={12}/> Decline
        </button>
      </div>
    );
  }
  if (fs === "accepted") {
    return (
      <div className="mt-4 flex items-center gap-2">
        {!confirmRemove ? (
          <button data-testid="pp-friends"
                  onClick={() => setConfirmRemove(true)}
                  className="px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm inline-flex items-center gap-2">
            <Check size={14}/> Friends · Remove
          </button>
        ) : (
          <>
            <span className="text-xs text-zinc-400">Remove friendship?</span>
            <button data-testid="pp-confirm-remove"
                    onClick={() => wrap(() => api.delete(`/friends/${fid}`))}
                    className="px-3 py-1.5 rounded-full bg-red-500/15 border border-red-500/40 text-red-300 text-xs inline-flex items-center gap-1.5">
              <UserMinus size={12}/> Confirm
            </button>
            <button onClick={() => setConfirmRemove(false)}
                    className="px-3 py-1.5 rounded-full border border-white/10 text-xs hover:bg-white/5">
              Cancel
            </button>
          </>
        )}
        <button data-testid="pp-block"
                onClick={() => wrap(() => api.post(`/friends/${uid}/block`))}
                className="px-3 py-2 rounded-full bg-white/5 border border-white/10 text-xs text-zinc-300 hover:bg-white/10 inline-flex items-center gap-1.5">
          <Ban size={12}/> Block
        </button>
      </div>
    );
  }
  if (fs === "blocked") {
    return (
      <div className="mt-4">
        <button data-testid="pp-unblock"
                onClick={() => wrap(() => api.post(`/friends/${uid}/unblock`))}
                className="px-4 py-2 rounded-full bg-white/10 border border-white/15 text-zinc-200 text-sm inline-flex items-center gap-2">
          Unblock
        </button>
      </div>
    );
  }
  return null;
}


function StatChip({ label, value, suffix }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[var(--surface)] px-3 py-2.5">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-lg font-black tabular-nums mt-0.5">
        {value}{suffix && <span className="text-xs text-zinc-500 font-bold ml-0.5">{suffix}</span>}
      </div>
    </div>
  );
}

function MatchRow({ m, index, ownerId }) {
  const date = m.finished_at ? new Date(m.finished_at) : null;
  const dateStr = date ? date.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" }) : "—";
  const players = (m.players || []).slice().sort((a, b) => (a.rank ?? 99) - (b.rank ?? 99));
  const me = players.find((p) => p.user_id === ownerId);
  const won = me && me.rank === 1;
  return (
    <motion.li initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.04 }}
      className={`grid grid-cols-[60px_1fr_auto] sm:grid-cols-[80px_1fr_140px_80px] gap-3 items-center px-4 py-3 rounded-xl border ${
        won ? "bg-[var(--accent)]/8 border-[var(--accent)]/30" : "bg-[var(--surface)] border-white/5"
      }`}>
      <div className="text-[11px] text-zinc-500 uppercase tracking-widest">{dateStr}</div>
      <div className="min-w-0">
        <div className="text-xs text-zinc-400 truncate">vs {players.filter(p => p.user_id !== ownerId).map(p => p.username).join(", ") || "—"}</div>
        <div className="text-[10px] text-zinc-600 uppercase tracking-widest mt-0.5">{m.settings?.difficulty || "—"} · {m.settings?.rounds_count || "?"} rounds</div>
      </div>
      <div className="hidden sm:flex items-center gap-2 text-[11px] uppercase tracking-widest">
        {won ? <span className="text-[var(--accent)] flex items-center gap-1 font-bold"><Trophy size={12} /> winner</span> : <span className="text-zinc-500">#{me?.rank || "—"} of {players.length}</span>}
      </div>
      <div className="text-right font-mono font-black tabular-nums text-[var(--text)]">{me?.score ?? 0}</div>
    </motion.li>
  );
}

export default function PublicProfile() {
  const { userId } = useParams();
  const [summary, setSummary] = useState(null);
  const [matches, setMatches] = useState([]);
  const [achievements, setAchievements] = useState({ unlocked: [], locked: [] });
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: s }, { data: ach }] = await Promise.all([
          api.get(`/users/${userId}/profile-summary`),
          api.get(`/users/${userId}/achievements`),
        ]);
        if (cancelled) return;
        setSummary(s);
        setAchievements({ unlocked: ach.unlocked, locked: [] });
        // Public match history surfaces only matches the viewed user played; for now we
        // re-use the global match history endpoint filtered on the client.
        try {
          const { data: mh } = await api.get(`/me/match-history?limit=10`);
          if (!cancelled) setMatches((mh.matches || []).filter(m =>
            (m.players || []).some(p => p.user_id === userId)
          ));
        } catch { /* not signed in or no overlap — ignore */ }
      } catch (e) {
        setErr(e?.response?.data?.detail || "User not found");
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  if (err) {
    return (
      <div data-testid="public-profile-error" className="min-h-screen bg-[var(--bg)] text-zinc-200 flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-3xl font-bold mb-2">Player unavailable</div>
          <div className="text-sm text-zinc-400">{err}</div>
          <Link to="/dashboard" className="mt-6 inline-block px-5 py-2.5 rounded-full bg-[var(--accent)] text-black font-semibold">Back to dashboard</Link>
        </div>
      </div>
    );
  }
  if (!summary) {
    return <div className="min-h-screen bg-[var(--bg)] text-zinc-200 flex items-center justify-center text-xs uppercase tracking-[0.4em]">loading profile…</div>;
  }

  const u = summary.user;
  const stats = summary.user_stats;
  const winRate = stats.games_played ? Math.round((stats.wins / stats.games_played) * 100) : 0;
  const accuracy = stats.total_answers ? Math.round((stats.total_correct / stats.total_answers) * 100) : 0;
  const flag = countryFlag(u.country);
  const initial = (u.username || "?")[0]?.toUpperCase();

  return (
    <div data-testid="public-profile-page" className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <header className="px-6 py-5 max-w-4xl mx-auto">
        <Link to="/dashboard" className="text-xs uppercase tracking-[0.4em] text-[var(--accent)] hover:brightness-125">← dashboard</Link>
      </header>
      <main className="max-w-4xl mx-auto px-6 pb-16 space-y-10">
        <motion.section initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col sm:flex-row gap-6 items-start">
          <Avatar user={u} size="xl" className="ring-2 ring-[var(--accent)]/40 rounded-full" />
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.4em] text-[var(--accent)]">public profile</div>
            <h1 data-testid="public-profile-username" className="text-4xl sm:text-5xl font-black mt-1.5 leading-tight truncate">
              <UserName user={u} /> {flag && (
                <span className="ml-3 text-3xl align-middle" title={countryName(u.country)} aria-label={countryName(u.country) || undefined}>{flag}</span>
              )}
            </h1>
            <div className="text-sm text-zinc-500 mt-1 truncate">
              {(u.first_name || u.last_name) && <span>{u.first_name} {u.last_name} · </span>}
              {countryName(u.country) || "—"}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 items-center">
              <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[var(--accent)]/10 border border-[var(--accent)]/30 text-[10px] uppercase tracking-widest text-zinc-400" data-testid="public-iq-chip">
                Football IQ <span className="text-[var(--accent)] font-bold">{stats.football_iq}</span>
              </span>
              {summary.is_online && (
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-emerald-400">
                  <span className="size-2 rounded-full bg-emerald-500" /> online
                </span>
              )}
            </div>
            <FriendshipActions
              summary={summary}
              onChange={async () => {
                const { data } = await api.get(`/users/${userId}/profile-summary`);
                setSummary(data);
              }}
            />
          </div>
          {summary.favorite_club && <div className="w-full sm:w-72"><TeamCard team={summary.favorite_club} testId="public-favourite-club" /></div>}
        </motion.section>

        <section className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <StatChip label="Games" value={stats.games_played} />
          <StatChip label="Win %" value={winRate} suffix="%" />
          <StatChip label="Accuracy" value={accuracy} suffix="%" />
          <StatChip label="Best streak" value={stats.best_streak} />
          <StatChip label="Career path W" value={stats.career_path_wins} />
          <StatChip label="Avg" value={fmtMs(stats.avg_response_ms)} />
        </section>

        <section>
          <RankedStatsCard ranked={summary.ranked} />
        </section>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs uppercase tracking-[0.3em] text-zinc-500">achievements</h2>
            <span className="text-[10px] text-zinc-500 uppercase tracking-widest">{achievements.unlocked.length} / 7</span>
          </div>
          {achievements.unlocked.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-zinc-500" data-testid="public-no-achievements">No achievements unlocked yet.</div>
          ) : (
            <AchievementGrid unlocked={achievements.unlocked} locked={[]} compact />
          )}
        </section>

        {matches.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs uppercase tracking-[0.3em] text-zinc-500">shared matches</h2>
              <span className="text-[10px] text-zinc-600 uppercase tracking-widest">{matches.length} recent</span>
            </div>
            <ul className="space-y-2">
              {matches.map((m, i) => <MatchRow key={m.id} m={m} index={i} ownerId={userId} />)}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
